import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface DesasociarPresupuestoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Nombre del presupuesto independiente vinculado. */
  nombrePresupuesto?: string;
  /** Número o etiqueta de la escritura, para el texto de confirmación. */
  escritura?: string;
  /** Sin permiso para borrar ítems, la opción de eliminarlos queda deshabilitada. */
  puedeEliminarItems: boolean;
  isLoading?: boolean;
  onConfirm: (eliminarItems: boolean) => Promise<void> | void;
}

type DestinoItems = 'conservar' | 'eliminar';

/**
 * Confirmación para cortar el vínculo entre una escritura y el presupuesto
 * importado, eligiendo qué pasa con los ítems de la escritura.
 */
export const DesasociarPresupuestoDialog: React.FC<DesasociarPresupuestoDialogProps> = ({
  open,
  onOpenChange,
  nombrePresupuesto,
  escritura,
  puedeEliminarItems,
  isLoading = false,
  onConfirm,
}) => {
  const [destino, setDestino] = useState<DestinoItems>('conservar');

  useEffect(() => {
    if (open) setDestino('conservar');
  }, [open]);

  const presupuesto = nombrePresupuesto ? `"${nombrePresupuesto}"` : 'el presupuesto';
  const deEscritura = escritura ? ` de la escritura ${escritura}` : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Desasociar presupuesto</DialogTitle>
          <DialogDescription>
            Se va a desasociar {presupuesto}{deEscritura}. Los cambios en la escritura dejan de
            reflejarse en el presupuesto, que conserva sus ítems y vuelve a estar disponible para
            importar.
          </DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-3">
          <legend className="mb-2 text-sm font-medium">¿Qué pasa con los ítems de la escritura?</legend>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="destino-items"
              className="mt-1"
              checked={destino === 'conservar'}
              onChange={() => setDestino('conservar')}
            />
            <span>
              <span className="font-medium">Conservarlos</span>
              <span className="block text-muted-foreground">
                Quedan en la escritura, sin vínculo con el presupuesto.
              </span>
            </span>
          </label>
          <label
            className={`flex items-start gap-2 text-sm ${puedeEliminarItems ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
            title={puedeEliminarItems ? undefined : 'No tiene permiso para eliminar ítems'}
          >
            <input
              type="radio"
              name="destino-items"
              className="mt-1"
              checked={destino === 'eliminar'}
              disabled={!puedeEliminarItems}
              onChange={() => setDestino('eliminar')}
            />
            <span>
              <span className="font-medium">Eliminarlos</span>
              <span className="block text-muted-foreground">
                La escritura queda sin ítems de presupuesto.
              </span>
            </span>
          </label>
        </fieldset>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void onConfirm(destino === 'eliminar')}
            disabled={isLoading}
          >
            Desasociar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
